import ConversationsClient from '@/client/mentor/ConversationsClient'
import type {ConversationIntent} from '@/components/assistant/types'
import type {Conversation} from '@/client/mentor/ConversationsClient'
import type {CursorResponse} from '@/client/mentor/types'
import type {InfiniteData} from '@tanstack/vue-query'
import type {MaybeRef} from 'vue'
import type {Maybe} from '@/types'
import {CONVERSATIONS_PAGE_SIZE, FIRST_PAGE_INDEX} from '@/components/assistant/constants'
import {PaginationDirection} from '@/client/mentor/types'
import {assertNonNil} from '@/utils/assert'
import {computed, toValue} from 'vue'
import {isNullOrUndefined} from '@/utils/utils'
import {useInfiniteQuery, useMutation, useQueryClient} from '@tanstack/vue-query'

type ConversationsPage = CursorResponse<Conversation, string>
type ConversationsInfiniteData = InfiniteData<ConversationsPage, string | undefined>

export function useAssistantConversations(
  companyId: MaybeRef<Maybe<number>>,
  options?: {
    enabled?: MaybeRef<boolean>
  }
) {
  const resolvedCompanyId = computed(() => toValue(companyId))
  const hasCompanyId = computed(() => !isNullOrUndefined(resolvedCompanyId.value))

  const queryClient = useQueryClient()
  const queryKey = computed(() => buildConversationQueryKey(resolvedCompanyId.value))

  const isEnabledByCaller = computed(() => toValue(options?.enabled) ?? true)
  const queryEnabled = computed(() => hasCompanyId.value && isEnabledByCaller.value)

  const conversations = computed(() => conversationsQuery.data.value?.pages.flatMap((page) => page.items) ?? [])

  const conversationsQuery = useInfiniteQuery({
    queryKey,
    enabled: queryEnabled,
    initialPageParam: undefined as string | undefined,
    queryFn: ({pageParam}) => {
      return ConversationsClient.getConversations(assertNonNil(resolvedCompanyId.value), {
        cursor: pageParam,
        limit: CONVERSATIONS_PAGE_SIZE,
        direction: PaginationDirection.DOWN,
      })
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    staleTime: 5 * 60 * 1_000,
  })

  const updateConversationByIdMutation = useMutation({
    mutationFn: async (conversationId: string) => {
      return ConversationsClient.getConversation(assertNonNil(resolvedCompanyId.value), conversationId)
    },
    onSuccess: (conversation) => {
      upsertConversationInCache(conversation)
    },
  })

  const patchConversationTitleMutation = useMutation({
    mutationFn: async ({conversationId, newTitle}: {conversationId: string; newTitle: string}) => {
      return ConversationsClient.updateConversation({
        id: conversationId,
        companyId: assertNonNil(resolvedCompanyId.value),
        title: newTitle,
      })
    },
    onSuccess: (conversation) => {
      upsertConversationInCache(conversation)
    },
  })

  const deleteConversationByIdMutation = useMutation({
    mutationFn: async (conversationId: string) => {
      return ConversationsClient.deleteConversation(assertNonNil(resolvedCompanyId.value), conversationId)
    },
    onSuccess: (deleted, conversationId) => {
      if (!deleted) {
        return
      }

      queryClient.setQueryData<ConversationsInfiniteData | undefined>(queryKey, (data) => {
        if (!data) return data
        return removeConversation(data, conversationId)
      })
    },
  })

  function upsertConversationInCache(conversation: Conversation) {
    queryClient.setQueryData<ConversationsInfiniteData | undefined>(queryKey, (data) => {
      if (!data) return data

      if (hasConversation(data, conversation.id)) {
        return replaceConversation(data, conversation.id, conversation)
      }

      if (data.pages.length === 0) {
        return data
      }

      return prependToNewestPage(data, conversation)
    })
  }

  const intent: ConversationIntent = {
    deleteConversationById: deleteConversationByIdMutation.mutateAsync,
    fetchNextPage: async () => {
      await conversationsQuery.fetchNextPage()
    },
    refetch: async () => {
      await conversationsQuery.refetch()
    },
    updateConversationById: updateConversationByIdMutation.mutateAsync,
    patchConversationTitle: (conversationId: string, newTitle: string) => {
      return patchConversationTitleMutation.mutateAsync({
        conversationId,
        newTitle,
      })
    },
  }

  return {
    conversations,
    isLoading: conversationsQuery.isLoading,
    isFetchingNextPage: conversationsQuery.isFetchingNextPage,
    hasNextPage: conversationsQuery.hasNextPage,
    intent,
  }
}

export function buildConversationQueryKey(companyId: Maybe<number>) {
  return ['mentor', 'conversations', companyId]
}

function prependToNewestPage(data: ConversationsInfiniteData, conversation: Conversation): ConversationsInfiniteData {
  const pages = [...data.pages]
  if (pages.length === 0) return data

  const newestPageIndex = FIRST_PAGE_INDEX
  const newestPage = pages[newestPageIndex]

  pages[newestPageIndex] = {
    ...newestPage,
    items: [conversation, ...newestPage.items],
  }

  return {
    ...data,
    pages,
  }
}

function replaceConversation(
  data: ConversationsInfiniteData,
  targetConversationId: string,
  replacement: Conversation
): ConversationsInfiniteData {
  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      items: page.items.map((item) => (item.id === targetConversationId ? replacement : item)),
    })),
  }
}

function hasConversation(data: ConversationsInfiniteData, conversationId: string): boolean {
  return data.pages.some((page) => page.items.some((item) => item.id === conversationId))
}

function removeConversation(data: ConversationsInfiniteData, targetConversationId: string): ConversationsInfiniteData {
  return {
    ...data,
    pages: data.pages
      .map((page) => ({
        ...page,
        items: page.items.filter((item) => item.id !== targetConversationId),
      }))
      .filter((page, pageIndex) => page.items.length > 0 || pageIndex === FIRST_PAGE_INDEX),
  }
}
